import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackAppView } from '../../utils/appClickTracking';
import { Button, ConfigProvider, Input, Modal as AntModal, Tooltip } from 'antd';
import {
  AlertTriangle,
  Check,
  Clipboard,
  Edit3,
  KeyRound,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useApiKeyManager } from './ApiKeyManagerProvider';
import { createApiKey, renameApiKey, revokeApiKeyById, rotateApiKeyById } from '../../services/userApi';
import { MANAGED_KEY_LINE, desktopKeyLine, isDesktopKey, isManagedKey } from './managedKey';
import { useTheme } from '../../contexts/ThemeContext';
import { DELEGATE_ACCENT } from '../Delegate/delegateAccent';
import './ApiKeyManager.css';

// ------------------------------------------------------------------
// Name prefill — from the route the user is on
// ------------------------------------------------------------------

const DEFAULT_KEY_NAME = 'Key';

const suggestKeyName = (pathname, now = new Date()) => {
  const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const date = `${MONTH[now.getMonth()]} ${now.getDate()}`;
  let host = DEFAULT_KEY_NAME;
  if (!pathname) return `${host} — ${date}`;
  // Agent pages moved to /delegate/<agent> (Oct 2 2026); the old /connectors/delegations/* paths still match.
  if (/\/(delegations|delegate)\/cursor/.test(pathname)) host = 'Cursor';
  else if (/\/(delegations|delegate)\/claude-code/.test(pathname)) host = 'Claude Code';
  else if (/\/(delegations|delegate)\/openclaw/.test(pathname)) host = 'OpenClaw';
  return `${host} — ${date}`;
};

// ------------------------------------------------------------------
// Accent: the window wears the colour of the app that opened it (Nitish, Oct 2 2026). Opened anywhere
// under /delegate it is Delegate's grape; everywhere else (sidebar menu, Skills, marketing pages) teal.
// The window mounts once at the app root, outside every page's AppAccentTheme, so the route decides.
// --akm-key-tint = the accent tint flattened over the modal card (6% over #ffffff light, 8% over
// #1a1a1c dark) for the key line's scroll covers; teal reproduces the old 242,247,247 / 34,40,41.
// ------------------------------------------------------------------

const hexRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));

const akmAccent = (isDarkMode, fromDelegate) => {
  const hex = fromDelegate
    ? (isDarkMode ? DELEGATE_ACCENT.dark : DELEGATE_ACCENT.light)
    : (isDarkMode ? '#7dc3c3' : '#2e7e7e');
  const ink = isDarkMode ? (fromDelegate ? '#1d1d1f' : '#0a1a1a') : '#ffffff';
  const rgb = hexRgb(hex);
  const tint = isDarkMode
    ? rgb.map((c, i) => Math.round([26, 26, 28][i] * 0.92 + c * 0.08))
    : rgb.map((c) => Math.round(255 * 0.94 + c * 0.06));
  return {
    hex,
    ink,
    vars: {
      '--akm-accent': hex,
      '--akm-accent-rgb': rgb.join(', '),
      '--akm-on-accent': ink,
      '--akm-key-tint': tint.join(', '),
    },
  };
};

// ------------------------------------------------------------------
// Time-ago formatter for last_used_at
// ------------------------------------------------------------------

const formatRelativeTime = (isoString) => {
  if (!isoString) return 'Never';
  const now = new Date();
  const then = new Date(isoString);
  const deltaMs = now - then;
  const deltaMin = Math.floor(deltaMs / 60000);
  if (deltaMin < 1) return 'Just now';
  if (deltaMin < 60) return `${deltaMin} ${deltaMin === 1 ? 'minute' : 'minutes'} ago`;
  const deltaHr = Math.floor(deltaMin / 60);
  if (deltaHr < 24) return `${deltaHr} ${deltaHr === 1 ? 'hour' : 'hours'} ago`;
  const deltaDay = Math.floor(deltaHr / 24);
  if (deltaDay < 30) return `${deltaDay} ${deltaDay === 1 ? 'day' : 'days'} ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// ------------------------------------------------------------------
// The modal
//
// IMPORTANT — why we don't use Ant's message / Popconfirm here:
//   The overlay is z-index 9999. Ant's global message toasts (z-index
//   ~1010) and Popconfirm popovers (z-index ~1050) are hidden behind it.
//   Users click "Revoke" and nothing appears; they see a copy toast
//   that never reaches the screen.
//
// Fixes in this file:
//   - Copy button: transitions to "Copied ✓" for 2s, then resets.
//   - Revoke: inline confirmation row inside the row itself (no portal).
//   - Errors: rendered inline as an ErrorBanner inside the modal body.
//   - Ant Modal.confirm for "have you saved the key" still works because
//     its z-index inherits from the document root, and it stacks ABOVE
//     our overlay via Ant's internal z-index increment logic.
// ------------------------------------------------------------------

const ApiKeyManager = () => {
  const location = useLocation();
  const { isDarkMode, theme } = useTheme();
  const { isOpen, close, keys, loading, refresh } = useApiKeyManager();
  const fromDelegate = (location.pathname || '').startsWith('/delegate');
  const accent = akmAccent(isDarkMode, fromDelegate);
  const accentTheme = fromDelegate ? { token: { colorPrimary: accent.hex, colorTextLightSolid: accent.ink } } : undefined;
  // Denominator for the apikeys_* app_click elements (Sep 15 2026). Fires when the window OPENS, not on
  // mount (Oct 1 2026): this component is mounted once app-wide behind ApiKeyManagerProvider and stays
  // hidden, so the old mount-time useAppView counted every page load (marketing pages included) as a view.
  useEffect(() => { if (isOpen) trackAppView('apikeys'); }, [isOpen]);

  // ----- Local view state -----
  const [view, setView] = useState('list'); // 'list' | 'create-name' | 'create-result'
  const [nameInput, setNameInput] = useState('');
  const [creating, setCreating] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null); // { id, name, api_key, key_prefix }
  const [confirmSaved, setConfirmSaved] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');

  // NEW: in-row revoke confirmation state — keyed by row id so only
  // one row shows its confirm UI at a time
  const [revokeConfirmId, setRevokeConfirmId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);

  // Managed keys (the Subagents key) get Rotate instead of Revoke, with the same in-row confirm pattern.
  const [rotateConfirmId, setRotateConfirmId] = useState(null);
  const [rotatingId, setRotatingId] = useState(null);

  // NEW: inline copy feedback — button transitions to "Copied" for 2s
  const [copyJustSucceeded, setCopyJustSucceeded] = useState(false);

  // NEW: inline error banner. `null` hides it; a string renders a red banner.
  const [errorBanner, setErrorBanner] = useState(null);

  const overlayRef = useRef(null);
  const modalRef = useRef(null);
  const copyResetTimer = useRef(null);

  // Reset everything when the modal closes
  useEffect(() => {
    if (!isOpen) {
      setView('list');
      setNameInput('');
      setCreating(false);
      setNewlyCreatedKey(null);
      setConfirmSaved(false);
      setRenamingId(null);
      setRenameDraft('');
      setRevokeConfirmId(null);
      setRevokingId(null);
      setRotateConfirmId(null);
      setRotatingId(null);
      setCopyJustSucceeded(false);
      setErrorBanner(null);
      if (copyResetTimer.current) {
        clearTimeout(copyResetTimer.current);
        copyResetTimer.current = null;
      }
    }
  }, [isOpen]);

  // Clean up copy timer on unmount
  useEffect(() => {
    return () => {
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
    };
  }, []);

  // Keyboard: Esc closes, but only when NOT viewing the one-shot key display
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && view !== 'create-result') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, view, close]);

  // Scroll lock while modal is open
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Click on overlay (outside modal) to close — same guardrail as Esc
  const onOverlayClick = useCallback(
    (e) => {
      if (view === 'create-result') return;
      if (modalRef.current && !modalRef.current.contains(e.target)) close();
    },
    [view, close],
  );

  // Suggested prefill
  const suggestedName = useMemo(() => suggestKeyName(location?.pathname || ''), [location]);

  // ----- Create flow -----

  const enterCreateFlow = () => {
    setErrorBanner(null);
    setNameInput(suggestedName);
    setView('create-name');
  };

  const submitCreate = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) {
      setErrorBanner('Please name your key before generating.');
      return;
    }
    try {
      setCreating(true);
      setErrorBanner(null);
      const { data } = await createApiKey(trimmed);
      setNewlyCreatedKey(data);
      setConfirmSaved(false);
      setView('create-result');
      // Refresh list in background so when user returns to list, new key is there
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorBanner(detail || 'Failed to create API key. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const copyNewKey = async () => {
    if (!newlyCreatedKey?.api_key) return;
    try {
      await navigator.clipboard.writeText(newlyCreatedKey.api_key);
      setCopyJustSucceeded(true);
      // Reset after 2s — standard Github/Stripe/Linear pattern for "Copied" state
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
      copyResetTimer.current = setTimeout(() => {
        setCopyJustSucceeded(false);
        copyResetTimer.current = null;
      }, 2000);
    } catch {
      setErrorBanner('Copy failed — please select the key text manually and copy.');
    }
  };

  const finishCreateFlow = () => {
    if (!confirmSaved) {
      AntModal.confirm({
        title: 'Have you saved your API key?',
        content: "You won't see this key again. Make sure you've saved it somewhere safe.",
        okText: 'Yes, I saved it',
        cancelText: 'Go back',
        okButtonProps: fromDelegate ? { style: { background: accent.hex, borderColor: accent.hex, color: accent.ink } } : undefined,
        onOk: () => {
          setNewlyCreatedKey(null);
          setView('list');
        },
      });
      return;
    }
    setNewlyCreatedKey(null);
    setView('list');
  };

  // ----- Rename flow -----

  const startRename = (key) => {
    setErrorBanner(null);
    setRenamingId(key.id);
    setRenameDraft(key.name);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameDraft('');
  };

  const submitRename = async (keyId) => {
    const trimmed = renameDraft.trim();
    if (!trimmed) return cancelRename();
    try {
      setErrorBanner(null);
      await renameApiKey(keyId, trimmed);
      cancelRename();
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorBanner(detail || 'Failed to rename key. Please try again.');
    }
  };

  // ----- Revoke flow -----

  const startRevokeConfirm = (keyId) => {
    setErrorBanner(null);
    setRevokeConfirmId(keyId);
  };

  const cancelRevokeConfirm = () => {
    setRevokeConfirmId(null);
  };

  const submitRevoke = async (keyId) => {
    try {
      setRevokingId(keyId);
      setErrorBanner(null);
      await revokeApiKeyById(keyId);
      setRevokeConfirmId(null);
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorBanner(detail || 'Failed to revoke key. Please try again.');
    } finally {
      setRevokingId(null);
    }
  };

  // ----- Rotate flow (managed keys only) -----

  const startRotateConfirm = (keyId) => {
    setErrorBanner(null);
    setRevokeConfirmId(null);
    setRotateConfirmId(keyId);
  };

  const submitRotate = async (keyId) => {
    try {
      setRotatingId(keyId);
      setErrorBanner(null);
      await rotateApiKeyById(keyId);
      setRotateConfirmId(null);
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorBanner(detail || 'Failed to rotate key. Please try again.');
    } finally {
      setRotatingId(null);
    }
  };

  if (!isOpen) return null;

  const themeClass = isDarkMode ? 'akm-theme-dark' : 'akm-theme-light';

  return (
    <ConfigProvider theme={theme}>
    <ConfigProvider theme={accentTheme}>
      <div
        ref={overlayRef}
        className={`akm-overlay ${themeClass}`}
        style={accent.vars}
        onClick={onOverlayClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="akm-title"
      >
      <div className="akm-modal" ref={modalRef}>
        {/* Close button — hidden during create-result so plaintext stays visible */}
        {view !== 'create-result' && (
          <button
            type="button"
            className="akm-close"
            onClick={close}
            aria-label="Close API Key Manager"
          >
            <X size={18} />
          </button>
        )}

        {/* Inline error banner — renders ABOVE content so user sees it even
            when the overlay hides global Ant message toasts */}
        {errorBanner && (
          <div className="akm-error-banner" role="alert">
            <AlertTriangle size={15} />
            <span>{errorBanner}</span>
            <button
              type="button"
              className="akm-error-banner-close"
              onClick={() => setErrorBanner(null)}
              aria-label="Dismiss error"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* --- LIST VIEW --- */}
        {view === 'list' && (
          <>
            <div className="akm-eyebrow">Developer access</div>
            <h2 id="akm-title" className="akm-title">API Keys</h2>
            <p className="akm-subtitle">
              Create keys to authenticate your CellCog-powered agents, scripts, and integrations.
            </p>

            <div className="akm-primary-cta">
              <Button
                type="primary"
                size="large"
                icon={<Sparkles size={16} />}
                onClick={enterCreateFlow}
                data-track="apikeys_create_open"
              >
                Generate new key
              </Button>
            </div>

            {/* Empty state */}
            {!loading && keys.length === 0 && (
              <div className="akm-empty">
                <div className="akm-empty-icon">
                  <KeyRound size={28} />
                </div>
                <div className="akm-empty-title">You don't have an API key yet.</div>
                <div className="akm-empty-body">
                  Generate one to connect your agents.
                </div>
              </div>
            )}

            {/* List */}
            {keys.length > 0 && (
              <div className="akm-list">
                {keys.map((k) => {
                  const isRevoking = revokingId === k.id;
                  const isConfirmingRevoke = revokeConfirmId === k.id;
                  const managed = isManagedKey(k);
                  // A CellCog Desktop key is managed (no rename, no rotate) but its owner CAN remove it: that disconnects the computer.
                  const desktop = isDesktopKey(k);
                  const isRotating = rotatingId === k.id;
                  const isConfirmingRotate = rotateConfirmId === k.id;
                  return (
                    <div
                      key={k.id}
                      className={`akm-row ${isConfirmingRevoke || isConfirmingRotate ? 'akm-row-confirming' : ''}`}
                    >
                      <div className="akm-row-main">
                        {renamingId === k.id ? (
                          <div className="akm-rename-row">
                            <Input
                              autoFocus
                              value={renameDraft}
                              onChange={(e) => setRenameDraft(e.target.value)}
                              onPressEnter={() => submitRename(k.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') cancelRename();
                              }}
                              maxLength={64}
                              size="small"
                              style={{ maxWidth: 260 }}
                            />
                            <Button size="small" type="primary" onClick={() => submitRename(k.id)}>
                              Save
                            </Button>
                            <Button size="small" onClick={cancelRename}>
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <div className="akm-row-name-row">
                            <span className="akm-row-name">{k.name}</span>
                            {!managed && (
                              <Tooltip title="Rename">
                                <button
                                  type="button"
                                  className="akm-rename-btn"
                                  onClick={() => startRename(k)}
                                  aria-label={`Rename ${k.name}`}
                                >
                                  <Edit3 size={13} />
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        )}
                        <div className="akm-row-prefix">{k.key_prefix}</div>
                        <div className="akm-row-meta">
                          <span>Created {formatRelativeTime(k.created_at)}</span>
                          <span className="akm-row-meta-sep">·</span>
                          <span>Last used {formatRelativeTime(k.last_used_at)}</span>
                        </div>
                        {managed && (
                          <div className="akm-row-managed-note">
                            {desktop ? desktopKeyLine(k.device_hostname) : MANAGED_KEY_LINE}
                          </div>
                        )}
                      </div>

                      <div className="akm-row-actions">
                        {managed && !desktop ? (
                          isConfirmingRotate ? (
                            <div className="akm-revoke-confirm">
                              <span className="akm-revoke-confirm-label">Rotate? The old key stops working.</span>
                              <Button
                                size="small"
                                type="primary"
                                onClick={() => submitRotate(k.id)}
                                data-track="apikeys_rotate_confirm"
                                loading={isRotating}
                              >
                                Yes, rotate
                              </Button>
                              <Button
                                size="small"
                                onClick={() => setRotateConfirmId(null)}
                                disabled={isRotating}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="small"
                              type="text"
                              icon={<RefreshCw size={14} />}
                              onClick={() => startRotateConfirm(k.id)}
                              data-track="apikeys_rotate_open"
                            >
                              Rotate
                            </Button>
                          )
                        ) : isConfirmingRevoke ? (
                          <div className="akm-revoke-confirm">
                            <span className="akm-revoke-confirm-label">
                              {desktop ? 'Remove? That computer disconnects.' : 'Revoke?'}
                            </span>
                            <Button
                              size="small"
                              danger
                              onClick={() => submitRevoke(k.id)}
                              data-track={desktop ? 'apikeys_desktop_remove_confirm' : 'apikeys_revoke_confirm'}
                              loading={isRevoking}
                            >
                              {desktop ? 'Yes, remove' : 'Yes, revoke'}
                            </Button>
                            <Button
                              size="small"
                              onClick={cancelRevokeConfirm}
                              disabled={isRevoking}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="small"
                            danger
                            type="text"
                            icon={<Trash2 size={14} />}
                            onClick={() => startRevokeConfirm(k.id)}
                            data-track={desktop ? 'apikeys_desktop_remove_open' : 'apikeys_revoke_open'}
                          >
                            {desktop ? 'Remove' : 'Revoke'}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="akm-footer">
              <ShieldCheck size={14} />
              <span>Keys are hashed — we never store the plaintext. Revoke anytime.</span>
            </div>
          </>
        )}

        {/* --- CREATE-NAME VIEW --- */}
        {view === 'create-name' && (
          <>
            <div className="akm-eyebrow">New API key</div>
            <h2 className="akm-title">Name your key</h2>
            <p className="akm-subtitle">
              Give it a label so you can recognize it later. You can rename anytime.
            </p>

            <div className="akm-name-input-wrap">
              <Input
                size="large"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onPressEnter={submitCreate}
                maxLength={64}
                placeholder="e.g. Cursor — Apr 18"
                autoFocus
              />
            </div>

            <div className="akm-actions-row">
              <Button size="large" onClick={() => { setErrorBanner(null); setView('list'); }}>
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={submitCreate}
                data-track="apikeys_generate"
                loading={creating}
                icon={<Sparkles size={16} />}
              >
                Generate key
              </Button>
            </div>
          </>
        )}

        {/* --- CREATE-RESULT VIEW --- */}
        {view === 'create-result' && newlyCreatedKey && (
          <>
            <div className="akm-eyebrow">Key generated</div>
            <h2 className="akm-title">Save your key now</h2>
            <p className="akm-subtitle">
              This is the only time we'll show your key in plaintext. Copy it somewhere safe.
            </p>

            {/* Two-row stack, NOT a single flex row: the key is `sk_` + 64 hex
                = 67 chars and cannot share a line with the Copy button without
                wrapping mid-token. Header row owns the label + Copy; the key
                owns the full measure below it. */}
            <div className="akm-plaintext-block">
              <div className="akm-plaintext-head">
                <span className="akm-plaintext-label">Secret key</span>
                <Button
                  icon={copyJustSucceeded ? <Check size={14} /> : <Clipboard size={14} />}
                  onClick={copyNewKey}
                  data-track="apikeys_copy"
                  size="small"
                  // Visual confirmation — briefly green/success-styled during "Copied"
                  className={copyJustSucceeded ? 'akm-copy-btn akm-copy-btn-success' : 'akm-copy-btn'}
                >
                  {copyJustSucceeded ? 'Copied' : 'Copy'}
                </Button>
              </div>
              {/* tabIndex: on narrow viewports this line scrolls horizontally,
                  and a scrollable region must be keyboard-reachable (WCAG 2.1.1). */}
              <div
                className="akm-plaintext-keyline"
                tabIndex={0}
                role="group"
                aria-label="Your new API key"
              >
                <code>{newlyCreatedKey.api_key}</code>
              </div>
            </div>

            <div className="akm-warning-inline">
              <AlertTriangle size={15} />
              <span>
                Once you close this view, the plaintext key cannot be recovered. If you lose
                it, revoke this one and generate a new one.
              </span>
            </div>

            <div className="akm-save-check" onClick={() => setConfirmSaved(!confirmSaved)}>
              <span className={`akm-save-check-box ${confirmSaved ? 'checked' : ''}`}>
                {confirmSaved && <Check size={12} strokeWidth={3} />}
              </span>
              <span>I have saved my API key securely</span>
            </div>

            <div className="akm-actions-row">
              <Button
                type="primary"
                size="large"
                onClick={finishCreateFlow}
                data-track="apikeys_done"
                disabled={!confirmSaved}
              >
                Done
              </Button>
            </div>
          </>
        )}
      </div>
      </div>
    </ConfigProvider>
    </ConfigProvider>
  );
};

export default ApiKeyManager;
